import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import './EmailTicketSettings.css';

const EmailTicketSettings = () => {
    const [activeTab, setActiveTab] = useState('keywords');

    // Keywords Data
    const [keywords, setKeywords] = useState([]);
    const [modules, setModules] = useState([]);
    const [keywordInput, setKeywordInput] = useState({ id: '', keyword: '', target_module_id: '' });

    // Keywords Datatable State
    const [keywordSearch, setKeywordSearch] = useState('');
    const [keywordSortConfig, setKeywordSortConfig] = useState({ key: 'id', direction: 'descending' });
    const [keywordCurrentPage, setKeywordCurrentPage] = useState(1);
    const [keywordsPerPage, setKeywordsPerPage] = useState(10);

    // Assignment Data
    const [assignments, setAssignments] = useState([]);
    const [employees, setEmployees] = useState([]);

    // Sort & Pagination state for Assignments
    const [assignmentSearch, setAssignmentSearch] = useState('');
    const [assignmentsSortConfig, setAssignmentsSortConfig] = useState({ key: 'module_name', direction: 'ascending' });
    const [assignmentsCurrentPage, setAssignmentsCurrentPage] = useState(1);
    const [assignmentsPerPage, setAssignmentsPerPage] = useState(10);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null, keyword: '' });

    // --- KEYWORDS PAGINATION & SORTING ---
    const filteredKeywords = useMemo(() => {
        if (!keywordSearch) return keywords;
        return keywords.filter(kw =>
            kw.keyword.toLowerCase().includes(keywordSearch.toLowerCase()) ||
            (kw.module_name && kw.module_name.toLowerCase().includes(keywordSearch.toLowerCase()))
        );
    }, [keywords, keywordSearch]);

    const sortedKeywords = useMemo(() => {
        let sortable = [...filteredKeywords];
        if (keywordSortConfig !== null) {
            sortable.sort((a, b) => {
                if (a[keywordSortConfig.key] < b[keywordSortConfig.key]) {
                    return keywordSortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[keywordSortConfig.key] > b[keywordSortConfig.key]) {
                    return keywordSortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortable;
    }, [filteredKeywords, keywordSortConfig]);

    const paginatedKeywords = useMemo(() => {
        const startIndex = (keywordCurrentPage - 1) * keywordsPerPage;
        return sortedKeywords.slice(startIndex, startIndex + keywordsPerPage);
    }, [sortedKeywords, keywordCurrentPage, keywordsPerPage]);

    const totalKeywordPages = Math.ceil(sortedKeywords.length / keywordsPerPage);

    const handleKeywordSort = (key) => {
        let direction = 'ascending';
        if (keywordSortConfig && keywordSortConfig.key === key && keywordSortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setKeywordSortConfig({ key, direction });
    };

    // --- ASSIGNMENTS PAGINATION & SORTING ---
    const filteredAssignments = useMemo(() => {
        if (!assignmentSearch) return assignments;
        return assignments.filter(mod =>
            mod.module_name && mod.module_name.toLowerCase().includes(assignmentSearch.toLowerCase())
        );
    }, [assignments, assignmentSearch]);

    const sortedAssignments = useMemo(() => {
        let sortable = [...filteredAssignments];
        if (assignmentsSortConfig !== null) {
            sortable.sort((a, b) => {
                if (a[assignmentsSortConfig.key] < b[assignmentsSortConfig.key]) {
                    return assignmentsSortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[assignmentsSortConfig.key] > b[assignmentsSortConfig.key]) {
                    return assignmentsSortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortable;
    }, [filteredAssignments, assignmentsSortConfig]);

    const paginatedAssignments = useMemo(() => {
        const startIndex = (assignmentsCurrentPage - 1) * assignmentsPerPage;
        return sortedAssignments.slice(startIndex, startIndex + assignmentsPerPage);
    }, [sortedAssignments, assignmentsCurrentPage, assignmentsPerPage]);

    const handleAssignmentsSort = (key) => {
        let direction = 'ascending';
        if (assignmentsSortConfig && assignmentsSortConfig.key === key && assignmentsSortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setAssignmentsSortConfig({ key, direction });
    };

    const totalAssignmentPages = Math.ceil(sortedAssignments.length / assignmentsPerPage);

    useEffect(() => {
        if (activeTab === 'keywords') fetchKeywords();
        if (activeTab === 'assignments') fetchAssignments();
    }, [activeTab]);

    const fetchKeywords = async () => {
        try {
            const { data } = await api.get('/email-settings/keywords');
            if (data.status) {
                setKeywords(data.data.keywords);
                setModules(data.data.modules);
            }
        } catch (error) {
            toast.error("Failed to fetch keywords.");
        }
    };

    const fetchAssignments = async () => {
        try {
            const { data } = await api.get('/email-settings/assignments');
            if (data.status) {
                setAssignments(data.data.modules); // Returns list of modules mapped with employee_id
                setEmployees(data.data.employees);
            }
        } catch (error) {
            toast.error("Failed to fetch assignments.");
        }
    };

    // KEYWORD HANDLERS
    const handleSaveKeyword = async (e) => {
        e.preventDefault();
        if (!keywordInput.keyword || !keywordInput.target_module_id) {
            toast.warning('Keyword and Module are required!');
            return;
        }

        try {
            const { data } = await api.post('/email-settings/save-keyword', keywordInput);
            if (data.status) {
                toast.success(data.message);
                setKeywordInput({ id: '', keyword: '', target_module_id: '' });
                setKeywordCurrentPage(1); // Reset to first page to see the new entry
                fetchKeywords();
            }
        } catch (error) {
            toast.error('Error saving keyword.');
        }
    };

    const handleDeleteKeyword = (kw) => {
        setDeleteModal({ isOpen: true, id: kw.id, keyword: kw.keyword });
    };

    const confirmDeleteKeyword = async () => {
        const { id } = deleteModal;
        try {
            const { data } = await api.delete(`/email-settings/delete-keyword/${id}`);
            if (data.status) {
                toast.success(data.message);
                setDeleteModal({ isOpen: false, id: null, keyword: '' });
                fetchKeywords();
            }
        } catch (error) {
            toast.error('Error deleting keyword.');
        }
    };

    const handleEditKeyword = (kw) => {
        setKeywordInput({ id: kw.id, keyword: kw.keyword, target_module_id: kw.target_module_id });
    };

    // ASSIGNMENT HANDLERS
    const handleAssignmentChange = async (moduleId, selectedOptions) => {
        try {
            const employeeIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
            const { data } = await api.post('/email-settings/update-assignment', {
                module_id: moduleId,
                employee_ids: employeeIds
            });
            if (data.status) {
                toast.success(data.message);
                fetchAssignments(); // Refresh to show newly updated
            }
        } catch (error) {
            toast.error('Error updating assignment.');
        }
    };

    // Prepare options for react-select
    const employeeOptions = useMemo(() => {
        return employees.map(emp => ({
            value: emp.user_id,
            label: `${emp.first_name} ${emp.last_name || ''} (ID: ${emp.user_id})`
        }));
    }, [employees]);

    return (
        <div className="bg-white rounded-lg shadow-sm p-4 mx-2 mb-2 sm:p-6 sm:mx-6 sm:mb-6 mt-4 sm:mt-6 ring-1 ring-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-6">
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">Email Ticket Automation Settings</h2>
            </div>

            <div className="overflow-x-auto mb-6 scrollbar-hide">
                <ul className="flex border-b border-gray-200 space-x-2 min-w-max">
                    <li className="me-2">
                        <button
                            className={`inline-block p-3 sm:p-4 border-b-2 rounded-t-lg text-sm sm:text-base ${activeTab === 'keywords' ? 'text-red-600 border-red-600' : 'hover:text-gray-600 hover:border-gray-300 border-transparent'}`}
                            onClick={() => setActiveTab('keywords')}
                        >
                            Department Keywords
                        </button>
                    </li>
                    <li className="me-2">
                        <button
                            className={`inline-block p-3 sm:p-4 border-b-2 rounded-t-lg text-sm sm:text-base ${activeTab === 'assignments' ? 'text-red-600 border-red-600' : 'hover:text-gray-600 hover:border-gray-300 border-transparent'}`}
                            onClick={() => setActiveTab('assignments')}
                        >
                            Module Auto-Assignments
                        </button>
                    </li>
                </ul>
            </div>

            <div className="tab-content">
                {/* KEYWORDS TAB */}
                {activeTab === 'keywords' && (
                    <div className="space-y-6">
                        <div>
                            <h4 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 mb-2">Keyword to Module Mapping</h4>
                            <p className="text-sm text-gray-500 mb-4 font-medium italic">
                                Connections are being synced from: <span className="font-bold text-gray-800 underline">server@enovasolutions.com</span>.
                                <br />
                                If keywords are added and assigned to a module, then it is automatically assigned a to the respective employees.
                            </p>
                        </div>

                        <form className="mb-6 flex flex-col sm:flex-row items-stretch sm:items-end gap-4" onSubmit={handleSaveKeyword}>
                            <div className="w-full sm:w-64">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Keyword</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                                    placeholder="e.g. server down"
                                    value={keywordInput.keyword}
                                    onChange={e => setKeywordInput({ ...keywordInput, keyword: e.target.value })}
                                />
                            </div>
                            <div className="w-full sm:w-72">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Target Module</label>
                                <select
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white"
                                    value={keywordInput.target_module_id}
                                    onChange={e => setKeywordInput({ ...keywordInput, target_module_id: e.target.value })}
                                >
                                    <option value="">-- Select Module --</option>
                                    {modules.map(m => (
                                        <option key={m.module_id} value={m.module_id}>{m.module_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 min-w-0 sm:min-w-[150px]">
                                <button type="submit" className="w-full sm:w-auto inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                                    {keywordInput.id ? 'Update' : 'Add Keyword'}
                                </button>
                                {keywordInput.id && (
                                    <button type="button" className="w-full sm:w-auto inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" onClick={() => setKeywordInput({ id: '', keyword: '', target_module_id: '' })}>Cancel</button>
                                )}
                            </div>
                        </form>

                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                            <h4 className="text-lg font-medium text-gray-800">Keywords List</h4>
                            <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <div className="relative">
                                        <select
                                            className="appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-red-500 focus:border-red-500 outline-none bg-white font-semibold text-gray-700 cursor-pointer shadow-sm min-w-[70px]"
                                            value={keywordsPerPage}
                                            onChange={(e) => {
                                                setKeywordsPerPage(Number(e.target.value));
                                                setKeywordCurrentPage(1);
                                            }}
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={99999}>All</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                    <span className="font-medium text-gray-500">Entries</span>
                                </div>
                                <div className="relative w-full sm:w-64">
                                    <input
                                        type="text"
                                        placeholder="Search keywords or modules..."
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white"
                                        value={keywordSearch}
                                        onChange={(e) => {
                                            setKeywordSearch(e.target.value);
                                            setKeywordCurrentPage(1); // Reset to page 1 on search
                                        }}
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg flex flex-col">
                            <div className="overflow-x-auto lg:overflow-x-visible">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th
                                                className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleKeywordSort('keyword')}
                                            >
                                                Keyword {keywordSortConfig?.key === 'keyword' && (keywordSortConfig.direction === 'ascending' ? '↑' : '↓')}
                                            </th>
                                            <th
                                                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleKeywordSort('module_name')}
                                            >
                                                Target Module {keywordSortConfig?.key === 'module_name' && (keywordSortConfig.direction === 'ascending' ? '↑' : '↓')}
                                            </th>
                                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {paginatedKeywords.length > 0 ? paginatedKeywords.map(kw => (
                                            <tr key={kw.id}>
                                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{kw.keyword}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{kw.module_name} (ID: {kw.target_module_id})</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                                                    <button className="text-blue-600 hover:text-blue-900 mr-4 font-medium" onClick={() => handleEditKeyword(kw)}>Edit</button>
                                                    <button className="text-red-600 hover:text-red-900 font-medium" onClick={() => handleDeleteKeyword(kw)}>Delete</button>
                                                </td>
                                            </tr>
                                        )) : <tr><td colSpan="3" className="text-center py-4 text-sm text-gray-500">No keywords found matching "{keywordSearch}".</td></tr>}
                                    </tbody>
                                </table>
                            </div>

                            {/* KEYWORDS PAGINATION */}
                            {totalKeywordPages > 1 && (
                                <div className="sticky bottom-0 bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 z-20 rounded-b-lg shadow-[0_-8px_10px_-3px_rgba(0,0,0,0.05)]">
                                    <div className="flex-1 flex flex-col sm:flex-row items-center justify-between gap-4">
                                        <div>
                                            <p className="text-sm text-gray-700 text-center sm:text-left">
                                                Showing <span className="font-medium">{((keywordCurrentPage - 1) * keywordsPerPage) + 1}</span> to <span className="font-medium">{Math.min(keywordCurrentPage * keywordsPerPage, sortedKeywords.length)}</span> of <span className="font-medium">{sortedKeywords.length}</span> results
                                            </p>
                                        </div>
                                        <div>
                                            <nav className="relative z-0 inline-flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide" aria-label="Pagination">
                                                <button
                                                    onClick={() => setKeywordCurrentPage(prev => Math.max(prev - 1, 1))}
                                                    disabled={keywordCurrentPage === 1}
                                                    className="relative inline-flex items-center justify-center rounded border border-gray-200 bg-gray-50 text-[11px] font-bold text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                                                    style={{ width: '30px', height: '30px', minWidth: '30px' }}
                                                >
                                                    &lt;
                                                </button>
                                                {[...Array(totalKeywordPages)].map((_, i) => (
                                                    <button
                                                        key={i + 1}
                                                        onClick={() => setKeywordCurrentPage(i + 1)}
                                                        className={`relative inline-flex items-center justify-center rounded border text-[11px] font-bold transition-colors ${keywordCurrentPage === i + 1 ? 'bg-red-700 border-red-700 text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                                                        style={{ width: '30px', height: '30px', minWidth: '30px' }}
                                                    >
                                                        {i + 1}
                                                    </button>
                                                ))}
                                                <button
                                                    onClick={() => setKeywordCurrentPage(prev => Math.min(prev + 1, totalKeywordPages))}
                                                    disabled={keywordCurrentPage === totalKeywordPages}
                                                    className="relative inline-flex items-center justify-center rounded border border-gray-200 bg-gray-50 text-[11px] font-bold text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                                                    style={{ width: '30px', height: '30px', minWidth: '30px' }}
                                                >
                                                    &gt;
                                                </button>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ASSIGNMENTS TAB */}
                {activeTab === 'assignments' && (
                    <div className="space-y-6">
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4">
                            <div className="flex-1">
                                <h4 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 mb-2">Module Auto-Assignments</h4>
                                <p className="text-sm text-gray-500">Select which employee will automatically take ownership of a ticket when it routes to a specific module.</p>
                            </div>
                            <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <div className="relative">
                                        <select
                                            className="appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-red-500 focus:border-red-500 outline-none bg-white font-semibold text-gray-700 cursor-pointer shadow-sm min-w-[70px]"
                                            value={assignmentsPerPage}
                                            onChange={(e) => {
                                                setAssignmentsPerPage(Number(e.target.value));
                                                setAssignmentsCurrentPage(1);
                                            }}
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={99999}>All</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                    <span className="font-medium text-gray-500">Entries</span>
                                </div>
                                <div className="relative w-full sm:w-64">
                                    <input
                                        type="text"
                                        placeholder="Search modules..."
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-white"
                                        value={assignmentSearch}
                                        onChange={(e) => {
                                            setAssignmentSearch(e.target.value);
                                            setAssignmentsCurrentPage(1);
                                        }}
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg mt-6 flex flex-col">
                            <div className="overflow-x-auto lg:overflow-x-visible">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th
                                                className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleAssignmentsSort('module_name')}
                                            >
                                                Module Name {assignmentsSortConfig?.key === 'module_name' && (assignmentsSortConfig.direction === 'ascending' ? '↑' : '↓')}
                                            </th>
                                            <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Default Employee Assignment</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {paginatedAssignments.length > 0 ? paginatedAssignments.map(mod => {
                                            const selectedEmployees = employeeOptions.filter(opt => mod.employee_ids && mod.employee_ids.includes(Number(opt.value)));
                                            return (
                                                <tr key={mod.module_id}>
                                                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{mod.module_name}</td>
                                                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                        <Select
                                                            isMulti
                                                            options={employeeOptions}
                                                            value={selectedEmployees}
                                                            onChange={(selected) => handleAssignmentChange(mod.module_id, selected)}
                                                            className="w-full max-w-lg text-sm"
                                                            placeholder="-- Select Assignments --"
                                                            closeMenuOnSelect={false}
                                                            menuPortalTarget={document.body}
                                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                                        />
                                                    </td>
                                                </tr>
                                            )
                                        }) : <tr><td colSpan="2" className="text-center py-4 text-sm text-gray-500">No modules found.</td></tr>}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            {totalAssignmentPages > 1 && (
                                <div className="sticky bottom-0 bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 z-20 rounded-b-lg shadow-[0_-8px_10px_-3px_rgba(0,0,0,0.05)]">
                                    <div className="flex-1 flex flex-col sm:flex-row items-center justify-between gap-4">
                                        <div>
                                            <p className="text-sm text-gray-700 text-center sm:text-left">
                                                Showing <span className="font-medium">{((assignmentsCurrentPage - 1) * assignmentsPerPage) + 1}</span> to <span className="font-medium">{Math.min(assignmentsCurrentPage * assignmentsPerPage, sortedAssignments.length)}</span> of <span className="font-medium">{sortedAssignments.length}</span> results
                                            </p>
                                        </div>
                                        <div>
                                            <nav className="relative z-0 inline-flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide" aria-label="Pagination">
                                                <button
                                                    onClick={() => setAssignmentsCurrentPage(prev => Math.max(prev - 1, 1))}
                                                    disabled={assignmentsCurrentPage === 1}
                                                    className="relative inline-flex items-center justify-center rounded border border-gray-200 bg-gray-50 text-[11px] font-bold text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    style={{ width: '30px', height: '30px', minWidth: '30px' }}
                                                >
                                                    <span className="sr-only">Previous</span>
                                                    <span>&lt;</span>
                                                </button>
                                                {[...Array(totalAssignmentPages)].map((_, i) => (
                                                    <button
                                                        key={i}
                                                        onClick={() => setAssignmentsCurrentPage(i + 1)}
                                                        className={`relative inline-flex items-center justify-center rounded border text-[11px] font-bold transition-colors ${assignmentsCurrentPage === i + 1
                                                            ? 'bg-red-700 border-red-700 text-white'
                                                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                                                            }`}
                                                        style={{ width: '30px', height: '30px', minWidth: '30px' }}
                                                    >
                                                        {i + 1}
                                                    </button>
                                                ))}
                                                <button
                                                    onClick={() => setAssignmentsCurrentPage(prev => Math.min(prev + 1, totalAssignmentPages))}
                                                    disabled={assignmentsCurrentPage === totalAssignmentPages}
                                                    className="relative inline-flex items-center justify-center rounded border border-gray-200 bg-gray-50 text-[11px] font-bold text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    style={{ width: '30px', height: '30px', minWidth: '30px' }}
                                                >
                                                    <span className="sr-only">Next</span>
                                                    <span>&gt;</span>
                                                </button>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* DELETE CONFIRMATION MODAL */}
            {deleteModal.isOpen && (
                <div className="fixed inset-0 z-[100] overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setDeleteModal({ isOpen: false, id: null, keyword: '' })}></div>

                        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                        </svg>
                                    </div>
                                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                        <h3 className="text-base font-semibold leading-6 text-gray-900">Delete Keyword Mapping</h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                Are you sure you want to delete the mapping for "<span className="font-bold text-gray-900">{deleteModal.keyword}</span>"? This action cannot be undone.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <button
                                    type="button"
                                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 sm:ml-3 sm:w-auto"
                                    onClick={confirmDeleteKeyword}
                                >
                                    Delete Mapping
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                    onClick={() => setDeleteModal({ isOpen: false, id: null, keyword: '' })}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailTicketSettings;
